import { createApp, provide, h, type Directive } from 'vue';
import { DefaultApolloClient } from '@vue/apollo-composable';
import { apolloClient } from './apollo';
import { createPinia } from 'pinia';
import App from './App.vue';
import router from './router';
import PrimeVue from 'primevue/config';
import './assets/main.css';
import i18n from './i18n';
import { googleLog } from '@/stores/shared_store';
import * as Sentry from '@sentry/vue';

//theme
import './theme.css';

//core
import 'primevue/resources/primevue.min.css';

//primeflex
import 'primeflex/primeflex.css';

//icons
import 'primeicons/primeicons.css';
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  faSeedling,
  faLeaf,
  faFingerprint,
  faCreditCard,
} from '@fortawesome/free-solid-svg-icons';

import { registerSW } from 'virtual:pwa-register';
import { registerPwaInstallPromptListener } from '@/pwa/registerInstallPrompt';

// Register the service worker once the page has loaded and the browser is
// idle, so the precache download doesn't compete with the map tiles and
// data of the first visit. `immediate: true` is required here: without it
// registerSW waits for the window 'load' event, which has already fired.
function registerServiceWorkerWhenIdle() {
  const register = () => registerSW({ immediate: true });
  if ('requestIdleCallback' in window) {
    requestIdleCallback(register, { timeout: 5000 });
  } else {
    // Safari has no requestIdleCallback
    setTimeout(register, 3000);
  }
}
if (document.readyState === 'complete') {
  registerServiceWorkerWhenIdle();
} else {
  window.addEventListener('load', registerServiceWorkerWhenIdle, {
    once: true,
  });
}

const VOnClickOutside: Directive = {
  mounted: function (element, binding) {
    element.clickOutsideEvent = function (event: MouseEvent) {
      const excludeComponent = document.getElementById(binding.arg ?? '');

      if (
        !(element == event.target || element.contains(event.target)) &&
        !(
          excludeComponent &&
          (event.target == excludeComponent ||
            excludeComponent.contains(event.target as Node))
        )
      ) {
        binding.value(event, element);
      }
    };
    document.addEventListener('click', element.clickOutsideEvent);
  },
  unmounted: function (element) {
    document.removeEventListener('click', element.clickOutsideEvent);
  },
};

library.add(faSeedling);
library.add(faLeaf);
library.add(faFingerprint);
library.add(faCreditCard);
// components
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import Toast from 'primevue/toast';
import ToastService from 'primevue/toastservice';
import Tooltip from 'primevue/tooltip';
const app = createApp({
  render: () => h(App),
  setup() {
    provide(DefaultApolloClient, apolloClient);
  },
});
Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DNS,
  // Local dev and the E2E stack run on the Vite dev server; keep them out of the shared replay/error quota.
  enabled: !import.meta.env.DEV,
  // Both deployed builds are production builds, so the host tells prod (app.eatfresh.me) from dev (*.web.app).
  environment: window.location.hostname === 'app.eatfresh.me' ? 'production' : 'development',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.browserProfilingIntegration(),
    Sentry.browserApiErrorsIntegration(),
    Sentry.sessionTimingIntegration(),
    Sentry.replayIntegration(),

    Sentry.captureConsoleIntegration({ levels: ['error'] }),
    Sentry.rewriteFramesIntegration(),
    Sentry.extraErrorDataIntegration(),
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/europe-west3-eatfresh-cloud-connect\.cloudfunctions\.net\/eatfresh-api-dev/,
    /^https:\/\/europe-west3-eatfresh-cloud-connect\.cloudfunctions\.net\/eatfresh-api-prod/,
    'https://www.computop-paygate.com/payNow.aspx',
    'https://pay.google.com/gp/p/js/pay.js',
    'https://applepay.cdn-apple.com/jsapi/v1.1.0/apple-pay-sdk.js',
  ],
  // Session Replay
  replaysSessionSampleRate: 0.02, // 2% of all sessions
  replaysOnErrorSampleRate: 1.0, // 100% of sessions with an error
});
app.use(PrimeVue);
app.use(i18n);
app.use(router);
app.use(createPinia());
registerPwaInstallPromptListener();
console.log('VUE ROUTER MOUNTED');
app.use(ToastService);
app.provide(DefaultApolloClient, apolloClient);
app.component('font-awesome-icon', FontAwesomeIcon);
app.component('InputText', InputText);
app.component('PButton', Button);
// eslint-disable-next-line vue/multi-word-component-names
app.component('Toast', Toast);
app.directive('outside-click', VOnClickOutside);
app.directive('tooltip', Tooltip);

app.config.globalProperties.$googleLog = googleLog;

app.mount('#app');
